/**
 * Send a bulk action's ids in small sequential chunks.
 *
 * The bulk endpoints process their ids one after another server-side, so one
 * big request can outlive the API client's 30 s timeout (and nginx's 60 s
 * default) while the server keeps working - the operator would see "failed"
 * for leads that were in fact assigned. Small chunks keep every request short
 * and give the dialog real progress.
 *
 * If a chunk request itself fails (403, network), that chunk and every id not
 * yet sent are reported as failed with the error, and nothing more is sent -
 * the server-side checks are the same for every chunk, so retrying blindly
 * would only repeat the error.
 */
export const BULK_CHUNK_SIZE = 10;

export interface ChunkedRun<T> {
  results: T[];
  /** Ids that never got a server answer, with why. */
  unsent: Array<{ id: string; error: string }>;
}

export const runInChunks = async <T>(
  ids: ReadonlyArray<string>,
  send: (chunk: string[]) => Promise<T>,
  options: {
    onProgress?: (done: number, total: number) => void;
    errorText: (err: unknown) => string;
    chunkSize?: number;
  },
): Promise<ChunkedRun<T>> => {
  const size = options.chunkSize ?? BULK_CHUNK_SIZE;
  const unique = [...new Set(ids)];
  const results: T[] = [];
  options.onProgress?.(0, unique.length);
  for (let i = 0; i < unique.length; i += size) {
    const chunk = unique.slice(i, i + size);
    try {
      results.push(await send(chunk));
    } catch (err) {
      // The first chunk failing outright means nothing landed: surface it as
      // a request error (the caller toasts it) instead of N identical rows.
      if (results.length === 0) throw err;
      const error = options.errorText(err);
      return { results, unsent: unique.slice(i).map((id) => ({ id, error })) };
    }
    options.onProgress?.(Math.min(i + size, unique.length), unique.length);
  }
  return { results, unsent: [] };
};
